import { useEffect, useMemo, useState } from 'react';
import {
  Save,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  AlertCircle,
  Check,
  Presentation,
  Download,
  Image as ImageIcon,
  Film,
} from 'lucide-react';
import type { RecordModel } from 'pocketbase';
import { pb, errorText } from '../../lib/pb';
import { useCollection } from '../../lib/hooks';
import { listReviews } from '../../lib/api';
import { driveThumbnailUrl } from '../../lib/drive';
import { confirmDialog } from '../ConfirmDialog';
import type { Campaign, DemoBank, DemoScene, Product } from '../../../../shared/types';

interface SettingRecord extends RecordModel {
  key: string;
  value: unknown;
  default_value?: unknown;
}

const EMPTY_BANK: DemoBank = {
  delays: { scriptSeconds: 6, imageSecondsPerScene: 4, videoSecondsPerScene: 6, finishSeconds: 12 },
  simulatedCost: { image: 0.05, video: 0.4 },
  scenes: [],
  finalVideoUrl: '',
  finalVideoUrlUncaptioned: '',
};

const EMPTY_SCENE: DemoScene = {
  action: '',
  speech: '',
  location_tag: '',
  holding_product: 'NO',
  images: [''],
  videos: [''],
};

/** Tolerates a half-filled or older-shaped record. */
function normalise(raw: unknown): DemoBank {
  const v = (raw && typeof raw === 'object' ? raw : {}) as Partial<DemoBank>;
  return {
    delays: { ...EMPTY_BANK.delays, ...(v.delays ?? {}) },
    simulatedCost: { ...EMPTY_BANK.simulatedCost, ...(v.simulatedCost ?? {}) },
    scenes: Array.isArray(v.scenes)
      ? v.scenes.map((s) => ({
          ...EMPTY_SCENE,
          ...s,
          images: Array.isArray(s.images) && s.images.length ? s.images : [''],
          videos: Array.isArray(s.videos) && s.videos.length ? s.videos : [''],
        }))
      : [],
    finalVideoUrl: v.finalVideoUrl ?? '',
    finalVideoUrlUncaptioned: v.finalVideoUrlUncaptioned ?? '',
  };
}

/** Blank URL slots are dropped on save so the worker never sees them. */
function clean(bank: DemoBank): DemoBank {
  return {
    ...bank,
    scenes: bank.scenes.map((s) => ({
      ...s,
      action: s.action.trim(),
      speech: s.speech.trim(),
      location_tag: s.location_tag.trim(),
      images: s.images.map((u) => u.trim()).filter(Boolean),
      videos: s.videos.map((u) => u.trim()).filter(Boolean),
    })),
    finalVideoUrl: bank.finalVideoUrl.trim(),
    finalVideoUrlUncaptioned: bank.finalVideoUrlUncaptioned.trim(),
  };
}

const ROUND_LABEL = (i: number) => (i === 0 ? '1st render' : `after re-render #${i}`);

/**
 * The pre-made material a demo campaign plays back: the script, one or more
 * images and clips per scene (one per review round), and the finished cuts.
 * Stored as the `demo` settings row; the worker reads it through getSettings.
 */
export default function DemoBankEditor() {
  const settings = useCollection<SettingRecord>('settings', { filter: 'key = "demo"' });
  const record = settings.items[0] ?? null;

  const [bank, setBank] = useState<DemoBank>(EMPTY_BANK);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (record && !dirty) setBank(normalise(record.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id, record?.updated]);

  const update = (fn: (b: DemoBank) => DemoBank) => {
    setBank((b) => fn(b));
    setDirty(true);
    setSaved(false);
  };

  const updateScene = (i: number, fn: (s: DemoScene) => DemoScene) =>
    update((b) => ({ ...b, scenes: b.scenes.map((s, j) => (j === i ? fn(s) : s)) }));

  const moveScene = (i: number, dir: -1 | 1) =>
    update((b) => {
      const scenes = b.scenes.slice();
      const j = i + dir;
      if (j < 0 || j >= scenes.length) return b;
      [scenes[i], scenes[j]] = [scenes[j]!, scenes[i]!];
      return { ...b, scenes };
    });

  const save = async () => {
    if (!record) return;
    setBusy(true);
    setError(null);
    try {
      await pb.collection('settings').update(record.id, { value: clean(bank) });
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await settings.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const problems = useMemo(() => {
    const out: string[] = [];
    if (!bank.scenes.length) out.push('Add at least one scene.');
    bank.scenes.forEach((s, i) => {
      if (!s.speech.trim()) out.push(`Scene ${i + 1} has no speech.`);
      if (!s.images.some((u) => u.trim())) out.push(`Scene ${i + 1} has no image.`);
      if (!s.videos.some((u) => u.trim())) out.push(`Scene ${i + 1} has no clip.`);
    });
    if (!bank.finalVideoUrl.trim() && !bank.finalVideoUrlUncaptioned.trim()) {
      out.push('Add a final video link (captioned, uncaptioned, or both).');
    }
    return out;
  }, [bank]);

  if (settings.loading) {
    return <div className="zen-card p-8 text-xs text-natural-text/45">Loading demo assets…</div>;
  }
  if (!record) {
    return (
      <div className="zen-card p-6 text-xs text-amber-700">
        The <code className="font-mono">demo</code> settings row does not exist yet. Run{' '}
        <code className="font-mono">docker compose run --rm worker npm run setup</code> once to
        create it, then reload.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="zen-card p-6 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-serif text-lg font-medium flex items-center gap-2">
              <Presentation size={16} className="text-purple-600" />
              Demo assets
            </h2>
            <p className="text-xs text-natural-text/55 mt-1 leading-relaxed max-w-2xl">
              What a <strong>demo campaign</strong> plays back instead of generating anything. Create
              one from the Video Generator with the admin-only “Demo campaign” box ticked: the
              script below appears as if written (or split), each scene shows its image, then its
              clip, and the campaign ends on the final video. Demo campaigns are visible to admins
              only and never count in Statistics. Use <em>Reset demo</em> on the campaign to replay it.
            </p>
          </div>
          <SaveButton busy={busy} dirty={dirty} saved={saved} onClick={() => void save()} />
        </div>

        {error && (
          <div className="p-3 bg-red-50 text-red-600 border border-red-200 rounded-xl text-xs flex items-start gap-2">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {problems.length > 0 && (
          <div className="p-3 bg-amber-50 text-amber-800 border border-amber-200 rounded-xl text-[11px] leading-relaxed">
            <p className="font-semibold mb-1">A demo run will stop at the first gap:</p>
            <ul className="list-disc pl-4 space-y-0.5">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        )}

        <ImportFromCampaign onImport={(next) => update(() => next)} current={bank} />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="zen-card p-5 space-y-3">
          <h3 className="font-serif text-sm font-medium">Timing</h3>
          <p className="text-[11px] text-natural-text/50 leading-relaxed">
            How long each stage pretends to work. Long enough to talk over, short enough not to bore.
          </p>
          <NumberField
            label="Writing the script (seconds)"
            value={bank.delays.scriptSeconds}
            onChange={(n) => update((b) => ({ ...b, delays: { ...b.delays, scriptSeconds: n } }))}
          />
          <NumberField
            label="Per image (seconds)"
            value={bank.delays.imageSecondsPerScene}
            onChange={(n) => update((b) => ({ ...b, delays: { ...b.delays, imageSecondsPerScene: n } }))}
          />
          <NumberField
            label="Per clip (seconds)"
            value={bank.delays.videoSecondsPerScene}
            onChange={(n) => update((b) => ({ ...b, delays: { ...b.delays, videoSecondsPerScene: n } }))}
          />
          <NumberField
            label="Assemble → captions → publish, total (seconds)"
            value={bank.delays.finishSeconds}
            onChange={(n) => update((b) => ({ ...b, delays: { ...b.delays, finishSeconds: n } }))}
          />
        </div>

        <div className="space-y-5">
          <div className="zen-card p-5 space-y-3">
            <h3 className="font-serif text-sm font-medium">Pretend prices</h3>
            <p className="text-[11px] text-natural-text/50 leading-relaxed">
              Shown in the “Current cost of video” pill so the audience sees spend tick up. Never
              counted in Statistics.
            </p>
            <NumberField
              label="Per image (USD)"
              step={0.01}
              value={bank.simulatedCost.image}
              onChange={(n) => update((b) => ({ ...b, simulatedCost: { ...b.simulatedCost, image: n } }))}
            />
            <NumberField
              label="Per clip (USD)"
              step={0.01}
              value={bank.simulatedCost.video}
              onChange={(n) => update((b) => ({ ...b, simulatedCost: { ...b.simulatedCost, video: n } }))}
            />
          </div>

          <div className="zen-card p-5 space-y-3">
            <h3 className="font-serif text-sm font-medium">Final video</h3>
            <p className="text-[11px] text-natural-text/50 leading-relaxed">
              Google Drive share links work (the player falls back to Drive's embedded player). If
              only one is given it is used for both cuts.
            </p>
            <div>
              <label className="zen-label">With captions</label>
              <input
                value={bank.finalVideoUrl}
                onChange={(e) => update((b) => ({ ...b, finalVideoUrl: e.target.value }))}
                placeholder="https://drive.google.com/file/d/…/view"
                className="zen-input text-xs"
              />
            </div>
            <div>
              <label className="zen-label">Without captions</label>
              <input
                value={bank.finalVideoUrlUncaptioned}
                onChange={(e) => update((b) => ({ ...b, finalVideoUrlUncaptioned: e.target.value }))}
                placeholder="https://drive.google.com/file/d/…/view"
                className="zen-input text-xs"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-serif text-base font-medium">Scenes ({bank.scenes.length})</h3>
            <p className="text-[11px] text-natural-text/50 leading-relaxed max-w-2xl mt-0.5">
              The script exactly as it should appear for review. Each scene lists its media by
              review round: the first link is what the first render shows; the second is what a
              “re-render” request produces; and so on. A scene with a single link shows the same
              media again on re-render. “Re-render image” from the video screen uses the next
              image <em>and</em> the next clip.
            </p>
          </div>
          <button
            onClick={() => update((b) => ({ ...b, scenes: [...b.scenes, { ...EMPTY_SCENE }] }))}
            className="zen-btn-ghost text-xs shrink-0"
          >
            <Plus size={13} />
            Add scene
          </button>
        </div>

        {bank.scenes.length === 0 && (
          <div className="zen-card p-8 text-center text-xs text-natural-text/45">
            No scenes yet. Add them by hand, or import a finished campaign above.
          </div>
        )}

        {bank.scenes.map((scene, i) => (
          <div key={i} className="zen-card p-5 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h4 className="font-serif text-sm font-medium">Scene {i + 1}</h4>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => moveScene(i, -1)}
                  disabled={i === 0}
                  className="zen-btn-ghost py-1 px-2 disabled:opacity-30"
                  title="Move up"
                >
                  <ArrowUp size={12} />
                </button>
                <button
                  onClick={() => moveScene(i, 1)}
                  disabled={i === bank.scenes.length - 1}
                  className="zen-btn-ghost py-1 px-2 disabled:opacity-30"
                  title="Move down"
                >
                  <ArrowDown size={12} />
                </button>
                <button
                  onClick={async () => {
                    if (
                      await confirmDialog({
                        title: `Remove scene ${i + 1}?`,
                        message: 'Its text and media links are removed from the demo bank. Nothing is saved until you press Save.',
                        confirmLabel: 'Remove',
                        danger: true,
                      })
                    ) {
                      update((b) => ({ ...b, scenes: b.scenes.filter((_, j) => j !== i) }));
                    }
                  }}
                  className="zen-btn-ghost py-1 px-2 text-red-500 hover:text-red-600"
                  title="Remove scene"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="zen-label">Speech</label>
                <textarea
                  value={scene.speech}
                  onChange={(e) => updateScene(i, (s) => ({ ...s, speech: e.target.value }))}
                  rows={3}
                  className="zen-input text-xs leading-relaxed resize-y"
                  placeholder="What the presenter says in this scene"
                />
                <p className="text-[11px] text-natural-text/40 mt-1">
                  {scene.speech.trim() ? `${scene.speech.trim().split(/\s+/).length} words` : ' '}
                </p>
              </div>
              <div>
                <label className="zen-label">Action (shot description)</label>
                <textarea
                  value={scene.action}
                  onChange={(e) => updateScene(i, (s) => ({ ...s, action: e.target.value }))}
                  rows={3}
                  className="zen-input text-xs leading-relaxed resize-y"
                  placeholder="The person speaks directly to the camera…"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="zen-label">Location tag</label>
                <input
                  value={scene.location_tag}
                  onChange={(e) => updateScene(i, (s) => ({ ...s, location_tag: e.target.value }))}
                  className="zen-input text-xs font-mono"
                  placeholder="e.g. kitchen"
                />
              </div>
              <div>
                <label className="zen-label">Holding the product</label>
                <div className="flex gap-1 p-1 bg-stone-100/80 rounded-xl">
                  {(['YES', 'NO'] as const).map((v) => (
                    <button
                      key={v}
                      onClick={() => updateScene(i, (s) => ({ ...s, holding_product: v }))}
                      className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                        scene.holding_product === v ? 'bg-white shadow-xs' : 'text-natural-text/60'
                      }`}
                    >
                      {v === 'YES' ? 'Yes' : 'No'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <MediaList
                kind="image"
                urls={scene.images}
                onChange={(images) => updateScene(i, (s) => ({ ...s, images }))}
              />
              <MediaList
                kind="video"
                urls={scene.videos}
                onChange={(videos) => updateScene(i, (s) => ({ ...s, videos }))}
              />
            </div>
          </div>
        ))}

        {bank.scenes.length > 0 && (
          <div className="flex justify-end">
            <SaveButton busy={busy} dirty={dirty} saved={saved} onClick={() => void save()} />
          </div>
        )}
      </div>
    </div>
  );
}

function SaveButton({
  busy,
  dirty,
  saved,
  onClick,
}: {
  busy: boolean;
  dirty: boolean;
  saved: boolean;
  onClick: () => void;
}) {
  return (
    <button onClick={onClick} disabled={busy || !dirty} className="zen-btn-primary shrink-0">
      {saved ? <Check size={14} /> : <Save size={14} />}
      {busy ? 'Saving…' : saved ? 'Saved' : 'Save demo assets'}
    </button>
  );
}

function NumberField({
  label,
  value,
  onChange,
  step = 1,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  step?: number;
}) {
  return (
    <div>
      <label className="zen-label">{label}</label>
      <input
        type="number"
        min={0}
        step={step}
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(Number(e.target.value))}
        className="zen-input text-xs w-40"
      />
    </div>
  );
}

function MediaList({
  kind,
  urls,
  onChange,
}: {
  kind: 'image' | 'video';
  urls: string[];
  onChange: (urls: string[]) => void;
}) {
  const Icon = kind === 'image' ? ImageIcon : Film;
  return (
    <div className="space-y-2">
      <label className="zen-label flex items-center gap-1.5">
        <Icon size={12} />
        {kind === 'image' ? 'Images' : 'Clips'} by review round
      </label>
      {urls.map((url, r) => (
        <div key={r} className="flex items-center gap-2">
          {kind === 'image' && url.trim() ? (
            <img
              src={driveThumbnailUrl(url.trim(), 'w100')}
              alt=""
              referrerPolicy="no-referrer"
              className="w-9 h-9 rounded-lg object-cover border border-natural-border bg-stone-100 shrink-0"
            />
          ) : (
            <div className="w-9 h-9 rounded-lg border border-dashed border-natural-border flex items-center justify-center text-natural-text/30 shrink-0">
              <Icon size={13} />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <input
              value={url}
              onChange={(e) => onChange(urls.map((u, j) => (j === r ? e.target.value : u)))}
              placeholder={kind === 'image' ? 'Drive or direct image link' : 'Drive or direct .mp4 link'}
              className="zen-input text-xs py-1.5"
            />
            <p className="text-[10px] text-natural-text/40 mt-0.5">{ROUND_LABEL(r)}</p>
          </div>
          <button
            onClick={() => onChange(urls.length > 1 ? urls.filter((_, j) => j !== r) : [''])}
            className="p-1.5 rounded-lg text-natural-text/35 hover:text-red-500 cursor-pointer shrink-0"
            title="Remove"
          >
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <button onClick={() => onChange([...urls, ''])} className="zen-btn-ghost py-1 px-2 text-[11px]">
        <Plus size={11} />
        Add a re-render {kind === 'image' ? 'image' : 'clip'}
      </button>
    </div>
  );
}

/**
 * The quickest way to fill the bank: pick a finished real campaign and copy its
 * script, the approved image and clip of every scene, and its final cuts. The
 * presenter then only has to add the alternates for the re-render story.
 */
function ImportFromCampaign({
  onImport,
  current,
}: {
  onImport: (bank: DemoBank) => void;
  current: DemoBank;
}) {
  const campaigns = useCollection<Campaign>('campaigns', {
    filter: 'stage = "done" && is_demo != true',
    sort: '-created',
    expand: 'product',
  });
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    const campaign = campaigns.items.find((c) => c.id === pick);
    if (!campaign) return;
    if (
      current.scenes.length &&
      !(await confirmDialog({
        title: 'Replace the current scenes?',
        message: `The ${current.scenes.length} scene(s) in the editor are replaced by campaign #${campaign.seq}'s script and media. Nothing is saved until you press Save.`,
        confirmLabel: 'Replace',
      }))
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const [images, videos] = await Promise.all([
        listReviews(campaign.id, 'image'),
        listReviews(campaign.id, 'video'),
      ]);
      const scenes: DemoScene[] = (campaign.scenes ?? [])
        .slice()
        .sort((a, b) => a.sceneIndex - b.sceneIndex)
        .map((s) => {
          const img = images.find((r) => r.scene_index === s.sceneIndex && !r.failed);
          const vid = videos.find((r) => r.scene_index === s.sceneIndex && !r.failed);
          return {
            action: s.action ?? '',
            speech: s.speech ?? '',
            location_tag: s.location_tag ?? '',
            holding_product: s.holding_product === 'YES' ? 'YES' : 'NO',
            images: [img?.drive_url || img?.media_url || ''],
            videos: [vid?.drive_url || vid?.media_url || ''],
          };
        });
      onImport({
        ...current,
        scenes,
        finalVideoUrl: campaign.final_video_url || current.finalVideoUrl,
        finalVideoUrlUncaptioned: campaign.final_video_url_uncaptioned || current.finalVideoUrlUncaptioned,
      });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-3 bg-natural-bg rounded-xl border border-natural-border/60 space-y-2">
      <p className="text-[11px] text-natural-text/60 leading-relaxed">
        <strong>Shortcut:</strong> copy a finished campaign's script, approved images, clips and
        final video into the editor, then add the re-render alternates by hand.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <select value={pick} onChange={(e) => setPick(e.target.value)} className="zen-input text-xs py-1.5 w-72">
          <option value="">Choose a finished campaign…</option>
          {campaigns.items.map((c) => {
            const product = c.expand?.product as Product | undefined;
            return (
              <option key={c.id} value={c.id}>
                #{c.seq} — {product?.name || c.title || 'Untitled'} ({(c.scenes ?? []).length} scenes)
              </option>
            );
          })}
        </select>
        <button onClick={() => void run()} disabled={!pick || busy} className="zen-btn-ghost text-xs">
          <Download size={12} />
          {busy ? 'Importing…' : 'Import into editor'}
        </button>
        {error && <span className="text-[11px] text-red-600">{error}</span>}
      </div>
    </div>
  );
}
